import React, {useEffect, useMemo, useState} from "react";
import {Prompt} from "react-router-dom";
import {Button, CircularProgress, Divider, IconButton, Typography} from "@mui/material";
import {
    Delete as DeleteIcon,
    Edit as EditIcon,
    Visibility as ViewIcon,
    NavigateBefore as PrevIcon,
    NavigateNext as NextIcon,
    RemoveCircle as RemoveIcon
} from "@mui/icons-material";
import {
    Cards,
    ConfirmDeleteDialog,
    FloatingAddButton,
    TabBar,
    TabPanel,
    VerticalDivider
} from "@atttomyx/react-components";
import {NodeCard, OfferingCard, TaxCard} from "../cards";
import {ChooseNodeDialog, OfferingDialog, TaxDialog} from "../dialogs"; // Added TaxDialog
import {Actions, Items} from "../node";
import {
    LabeledAddressForm,
    LabeledDateForm,
    LabeledEmailForm,
    LabeledPhoneForm,
    LabeledStringForm,
    LabeledUrlForm,
    NodeForm,
    TagsForm,
} from "../forms";
import {usePaging} from "@atttomyx/shared-hooks";
import {edgeService, nodeService, offeringService} from "../../services";
import {arrays, datetime, forms, sorting, users} from "@atttomyx/shared-utils";
import {confirm} from "@atttomyx/react-utils";
import {nodeUtils, offeringUtils} from "../../utils";
import {verbiage} from "@atttomyx/shared-constants";
import {EDGE_CATEGORY, EDGE_LABEL} from "../../constants";
import "./editNodePage.css";

const toData = (node) => {
    return {
        imageUrl: node.imageUrl,
        firstName: node.firstName,
        lastName: node.lastName,
        phones: arrays.copy(node.phones),
        emails: arrays.copy(node.emails),
        urls: arrays.copy(node.urls),
        addresses: arrays.copy(node.addresses),
        dates: arrays.copy(node.dates),
        others: arrays.copy(node.others),
        tags: arrays.copy(node.tags),
        valid: true,
    }
};

const EditNodePage = (props) => {
    const {
        snackbar, dimensions, match, unique, households, nodes, edges, offerings, filters, account, user,
        onSaveNode, onSaveEdge, onSaveOffering, onDeleteEdge, onDeleteOffering, onGoto
    } = props;
    const [node, setNode] = useState({});
    const [data, setData] = useState(toData({}));
    const [offering, setOffering] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [spinner, setSpinner] = useState(false);
    const [prevId, setPrevId] = useState(null);
    const [nextId, setNextId] = useState(null);
    const [tab, setTab] = useState(0);
    const [showAdd, setShowAdd] = useState(false);
    const [tax, setTax] = useState(null);
    const householdPaging = usePaging();
    const offeringsPaging = usePaging();

    const showOfferings = useMemo(() => {
        return user.roles.admin || user.roles.deacon;
    }, [user]);

    const idToNode = useMemo(() => {
        return arrays.getIdToEntity(nodes);
    }, [nodes]);

    const household = useMemo(() => {
        const array = [];

        if (node.id) {
            const nodeIds = households[node.id];

            if (nodeIds) {
                nodeIds.forEach((nodeId) => {
                    const connected = idToNode[nodeId];

                    if (connected) {
                        arrays.addTo(array, connected);
                    }
                });
            }
        }

        return array.sort(sorting.sortByLastNameAndFirstName);
    }, [households, idToNode, node]);

    const excludedIds = useMemo(() => {
        return household.map((node) => node.id);
    }, [household]);

    const donations = useMemo(() => {
        const ret = [];

        if (node.id) {
            const byYear = {};
            const currentYear = datetime.getYear(datetime.now());

            offerings.filter(offering => offering.nodeId === node.id)
            .forEach((offering) => {
                const year = datetime.getYear(offering.date);
                const array = byYear[year] || [];

                arrays.addTo(array, offering);
                byYear[year] = array;
            });

            for (const year in byYear) {
                arrays.addTo(ret, {
                    year: year,
                    offerings: byYear[year].sort(offeringUtils.sortOfferings),
                });
            }

            // This part seems to add current year offerings directly to the root array,
            // which might not be the intended behavior if you want them grouped by year.
            // If the intention is to show current year offerings separately, this is fine.
            // Otherwise, it might be a duplicate or a logic error.
            // For now, I'll assume it's intentional based on the original code.
            if (currentYear in byYear) {
                arrays.addAll(ret, byYear[currentYear]);
            }
        }

        return ret;
    }, [offerings, node]);

    const loadNode = (nodeId) => {
        const filtered = filters.filter(nodes);
        const node = arrays.findEntity(filtered, nodeId) || {};
        const ids = arrays.getPrevNextIds(filtered, node);

        setNode(node);
        setPrevId(ids.prevId);
        setNextId(ids.nextId);
    };

    const redirectTo = (nodeId) => {
        onGoto(nodeId);
        loadNode(nodeId);
    };

    useEffect(() => {
        const nodeId = match.params.id;

        loadNode(nodeId);
    }, []);

    useEffect(() => {
        setData(toData(node));
    }, [node]);

    const resetForm = () => {
        setData(toData(node));
    };

    const cleanForm = () => {
        setData(forms.cleanData(data));
    };

    const submitForm = () => {
        const modified = {
            imageUrl: data.imageUrl,
            firstName: data.firstName,
            lastName: data.lastName,
            phones: data.phones,
            emails: data.emails,
            urls: data.urls,
            addresses: data.addresses,
            dates: data.dates,
            others: data.others,
            tags: data.tags,
            metadata: node.metadata,
        };

        const success = (node) => {
            cleanForm();
            setSpinner(false);
            snackbar.setSuccess("Member saved");
            setNode(node);
            onSaveNode(node);
        };

        const failure = (err) => {
            setSpinner(false);
            snackbar.setError(err);
        };

        setSpinner(true);
        nodeService.updateNode(node.id, modified, success, failure);
    };

    const addToHousehold = (nodeId) => {
        const edge = {
            sourceNodeId: node.id,
            targetNodeId: nodeId,
            category: EDGE_CATEGORY,
            label: EDGE_LABEL,
        };

        const success = (edge) => {
            setSpinner(false);
            snackbar.setSuccess("Added to household");
            onSaveEdge(edge);
        };

        const failure = (err) => {
            setSpinner(false);
            snackbar.setError(err);
        };

        setSpinner(true);
        edgeService.createEdge(edge, success, failure);
    };

    const removeFromHousehold = (nodeId) => {
        const otherIds = arrays.copy(excludedIds);
        arrays.removeFrom(otherIds, nodeId);

        const edge = edges.find((edge) =>
            (edge.sourceNodeId === nodeId && arrays.contains(otherIds, edge.targetNodeId))
            || (edge.targetNodeId === nodeId && arrays.contains(otherIds, edge.sourceNodeId)));

        const success = (edgeId) => {
            setSpinner(false);
            snackbar.setSuccess("Removed from household");
            onDeleteEdge(edgeId);
        };

        const failure = (err) => {
            setSpinner(false);
            snackbar.setError(err);
        };

        if (edge) {
            setSpinner(true);
            edgeService.deleteEdge(edge.id, success, failure);
        }
    };

    const renderHousehold = () => {
        return <Cards
            items={household}
            paging={householdPaging}
            dimensions={dimensions}
            renderer={(connected) =>
                <NodeCard
                    key={connected.id}
                    node={connected}
                >
                    <IconButton
                        color="secondary"
                        title="Remove"
                        className="delete"
                        disabled={connected.id === node.id}
                        onClick={() => confirm.confirm(`Are you sure you want to remove ${users.getFullName(connected)}?`,
                            () => removeFromHousehold(connected.id))}
                    >
                        <RemoveIcon/>
                    </IconButton>
                    <Actions
                        node={connected}
                    />
                    <IconButton
                        color="primary"
                        title="Edit"
                        disabled={connected.id === node.id}
                        onClick={() => redirectTo(connected.id)}
                    >
                        <EditIcon/>
                    </IconButton>
                </NodeCard>}
        />
    };

    const modified = forms.differ(toData(node), data);

    return spinner ?
        <div className="edit-node-page">
            <CircularProgress size="80px"/>
        </div> :
        <div className="edit-node-page">
            <div className="cards">
                <div className="card">
                    <NodeForm
                        nodeId={node.id}
                        data={data}
                        onChange={setData}
                    />
                </div>
                <div className="card outlined">
                    <Items
                        options={unique.phones}
                        data={data}
                        onChange={setData}
                        label="Phone"
                        field="phones"
                        form={LabeledPhoneForm}
                        formatter={nodeUtils.formatPhone}
                    />
                </div>
                <div className="card outlined">
                    <Items
                        options={unique.emails}
                        data={data}
                        onChange={setData}
                        label="Email"
                        field="emails"
                        form={LabeledEmailForm}
                    />
                </div>
                <div className="card outlined">
                    <Items
                        options={unique.urls}
                        data={data}
                        onChange={setData}
                        label="URL"
                        field="urls"
                        form={LabeledUrlForm}
                    />
                </div>
                <div className="card outlined">
                    <Items
                        options={unique.addresses}
                        data={data}
                        onChange={setData}
                        label="Address"
                        field="addresses"
                        form={LabeledAddressForm}
                        toLines={nodeUtils.addressToLines}
                        toItem={nodeUtils.addressToItem}
                    />
                </div>
                <div className="card outlined">
                    <Items
                        options={unique.dates}
                        data={data}
                        onChange={setData}
                        label="Date"
                        field="dates"
                        form={LabeledDateForm}
                        formatter={datetime.getFullDate}
                    />
                </div>
                <div className="card outlined">
                    <Items
                        options={unique.others}
                        data={data}
                        onChange={setData}
                        label="Other"
                        field="others"
                        form={LabeledStringForm}
                    />
                </div>
                <div className="card outlined">
                    <TagsForm
                        options={unique.tags}
                        data={data}
                        onChange={setData}
                    />
                </div>
            </div>
            <div className="actions">
                <IconButton
                    color="secondary"
                    title="Previous member"
                    disabled={!prevId}
                    onClick={() => redirectTo(prevId)}
                >
                    <PrevIcon/>
                </IconButton>
                <IconButton
                    color="secondary"
                    title="Next member"
                    disabled={!nextId}
                    onClick={() => redirectTo(nextId)}
                >
                    <NextIcon/>
                </IconButton>
                <VerticalDivider/>
                <Button
                    color="secondary"
                    variant="text"
                    disabled={!modified}
                    onClick={resetForm}
                >
                    Undo
                </Button>
                <Button color="primary" size="large"
                        disabled={!data.valid || !modified || !node.id}
                        onClick={submitForm}>
                    Save
                </Button>
            </div>
            <Divider/>
            {showOfferings ? <>
                <TabBar
                    tabs={["Household", "Offerings"]}
                    value={tab}
                    onChange={setTab}
                />
                <TabPanel value={tab} index={0}>
                    {renderHousehold()}
                </TabPanel>
                <TabPanel value={tab} index={1}>
                    <Cards
                        items={donations}
                        paging={offeringsPaging}
                        dimensions={dimensions}
                        renderer={(item) => item.year ?
                            <TaxCard
                                key={"year_" + item.year}
                                year={item.year}
                                offerings={item.offerings}
                            >
                                <IconButton
                                    color="primary"
                                    title="View"
                                    onClick={() => setTax(item)}
                                >
                                    <ViewIcon/>
                                </IconButton>
                            </TaxCard> :
                            <OfferingCard
                                key={item.id}
                                offering={item}
                            >
                                <IconButton
                                    color="secondary"
                                    title="Delete"
                                    className="delete"
                                    onClick={() => setDeleting(item)}
                                >
                                    <DeleteIcon/>
                                </IconButton>
                                <IconButton
                                    color="primary"
                                    title="Edit"
                                    onClick={() => setOffering(item)}
                                >
                                    <EditIcon/>
                                </IconButton>
                            </OfferingCard>}
                    />
                </TabPanel>
            </> : <>
                <Typography variant="h5">
                    Household
                </Typography>
                {renderHousehold()}
            </>}
            <FloatingAddButton
                title={"Add " + (tab === 0 ? "to household" : "offering")}
                position={!dimensions.landscape || !dimensions.short ? "higher" : undefined}
                center={!dimensions.short}
                onClick={() => {
                    if (tab === 0) {
                        setShowAdd(true);

                    } else if (tab === 1 && showOfferings) {
                        setOffering({
                            nodeId: node.id,
                        });
                    }
                }}
            />
            {showAdd && tab === 0 ?
                <ChooseNodeDialog
                    title="Add to household"
                    households={households}
                    nodes={nodes}
                    excludedIds={excludedIds}
                    onCancel={() => setShowAdd(false)}
                    onOk={(nodeId) => {
                        setShowAdd(false);
                        addToHousehold(nodeId);
                    }}
                /> : null}
            {offering && tab === 1 && showOfferings ?
                <OfferingDialog
                    snackbar={snackbar}
                    unique={unique}
                    nodes={nodes}
                    offering={offering}
                    onCancel={() => setOffering(null)}
                    onSave={(offering) => {
                        setOffering(null);
                        onSaveOffering(offering);
                    }}
                    showNode={false}
                /> : null}
            {deleting && tab === 1 && showOfferings ?
                <ConfirmDeleteDialog
                    snackbar={snackbar}
                    title={`${deleting.category} ${deleting.type}`}
                    onCancel={() => setDeleting(null)}
                    onDelete={(offeringId) => {
                        setDeleting(null);
                        onDeleteOffering(offeringId);
                    }}
                    deleter={(success, failure) => offeringService.deleteOffering(deleting.id, success, failure)}
                /> : null}
            {tax && tab === 1 && showOfferings ?
                <TaxDialog
                    account={account}
                    node={node}
                    year={tax.year}
                    offerings={tax.offerings}
                    onCancel={() => setTax(null)}
                /> : null}
            <Prompt when={modified} message={verbiage.UNSAVED_CHANGES}/>
        </div>
};

export default EditNodePage;
